import PropTypes from 'prop-types';
import React, { useRef } from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { Button } from 'antd';
import { Modal, ProgressBar } from 'react-bootstrap';
import ModalsService from 'services/ModalsService';
import SecurityService from 'services/SecurityService';
import StatusBar from 'components/core/StatusBar';

/**
 * The modals component renders the top modal in the stack whenever it is pushed to the store.
 * TODO: support for custom modal dialogs via the action payload parameters.
 */
class Modals extends React.Component {

    static propTypes = {
        modals: PropTypes.array,
        userLoggedIn: PropTypes.bool,
        closeModal: PropTypes.func
    };

    getModalSize(modalConfig) {
        //Size must be one of: ["lg","large","sm","small"]
        let size = null;
        if (modalConfig.modalSize) {
            size = modalConfig.modalSize;
        }
        return size;
    }

    getTopModalConfig() {
        let topConfig = null;

        for (let i = this.props.modals.length - 1; i >= 0; i--) {
            const modalConfig = this.props.modals[i];
            if (modalConfig.modalType !== ModalsService.TYPE_STATUS_MESSAGE) {
                topConfig = modalConfig;
                break;
            }
        }
        return topConfig;
    }

    onOk(modalConfig) {
        const performOk = () => {
            if (modalConfig.onOk) {
                if (modalConfig.onOk() !== false) {
                    ModalsService.closeModal(modalConfig.id); //zoe was here
                }
            } else {
                ModalsService.closeModal(modalConfig.id);
            }
        };
        if (modalConfig.okDelay) {
            // We need a delay if we have modals that have an RIEComboBox because of the Select.Creatable state problem that
            // requires RIEComboBox to have a setTimeout in onBlur.  The modal dialog can't close until AFTER the
            // RIEComboBox finishes the delayed finishEditing, or else the last thing changed will not get updated if the
            // user navigates directly from the RIEComboBox to the OK/Close button.
            setTimeout(() => {
                performOk();
            }, modalConfig.okDelay);
        } else {
            performOk();
        }
    }

    onCancel(modalConfig) {
        if (modalConfig.onCancel) {
            if (modalConfig.onCancel() !== false) {
                ModalsService.closeModal(modalConfig.id);
            }
        } else {
            ModalsService.closeModal(modalConfig.id);
        }
    }

    onKeyDown = (modalConfig, event) => {
        if (event.keyCode === 13) { // Enter
            if (!event.isDefaultPrevented()) {
                this.onOk(modalConfig);
            }

        } else if (event.keyCode === 27) { // Escape
            // TODO:  should we close dialog on escape?
        }
    };

    // modalType, modalTitle, modalMessage
    renderProgress(modalConfig) {
        return (
            <div className="static-modal">
                <Modal.Dialog>
                    <Modal.Header>
                        <Modal.Title>{modalConfig.modalMessage}</Modal.Title>
                    </Modal.Header>

                    <Modal.Body>
                        <ProgressBar striped bsStyle="info" now={100} srOnly />
                    </Modal.Body>

                </Modal.Dialog>
            </div>
        );
    }

    /**
     * Renders a non-modal status bar that hovers over the top of the screen. Shows the top message
     * @param modalConfig
     * @returns {*}
     */
    renderStatusMessages() {
        let component = (<div />);
        if (this.props.modals.length > 0) {
            const messages = this.props.modals.reduce((acc, modalConfig) => {
                if (modalConfig.modalType === ModalsService.TYPE_STATUS_MESSAGE) {
                    acc.push(modalConfig);
                }
                return acc;
            }, []);

            component = <StatusBar messages={messages} userLoggedIn={this.props.userLoggedIn} />;
        }

        return component;
    }

    renderError(modalConfig) {
        const okFunction = this.onOk.bind(this, modalConfig);
        if (typeof modalConfig.errorDetails !== 'string') {
            modalConfig.errorDetails = JSON.stringify(modalConfig.errorDetails, null, 1);
        }
        let detailsBlock = '';
        if (modalConfig.errorDetails) {
            detailsBlock = (
                <div>
                    <div className="details-label">
                        Details:
                    </div>
                    <div className="error-details">
                        <pre>{modalConfig.errorDetails}</pre>
                    </div>
                </div>
            );
        }

        return (
            <div className="static-modal">
                <Modal.Dialog bsSize={this.getModalSize(modalConfig)}>
                    <Modal.Header>
                        <Modal.Title>{modalConfig.modalTitle}</Modal.Title>
                    </Modal.Header>

                    <Modal.Body>
                        {modalConfig.modalMessage}
                        {detailsBlock}
                    </Modal.Body>

                    <Modal.Footer>
                        <Button type="primary" onClick={okFunction} autoFocus>OK</Button>
                    </Modal.Footer>

                </Modal.Dialog>
            </div>
        );
    }

    renderConfirm(modalConfig) {
        const okFunction = this.onOk.bind(this, modalConfig);
        const cancelFunction = this.onCancel.bind(this, modalConfig);
        const enterFunction = this.onKeyDown.bind(this, modalConfig);
        const style = _.isNil(modalConfig.padding) ? {} : { padding: modalConfig.padding };
        const className = modalConfig.className ? modalConfig.className : null;
        const autoFocus = modalConfig.autoFocus;

        return (
            <div className="static-modal" onKeyDown={enterFunction}>
                <Modal.Dialog bsSize={this.getModalSize(modalConfig)}>
                    <Modal.Header>
                        <Modal.Title>{modalConfig.modalTitle}</Modal.Title>
                    </Modal.Header>

                    <Modal.Body className={className} style={style}>
                        {modalConfig.modalMessage}
                    </Modal.Body>

                    <Modal.Footer>
                        <Button type="primary" onClick={okFunction} autoFocus={autoFocus}>OK</Button>
                        <Button onClick={cancelFunction}>Cancel</Button>
                    </Modal.Footer>

                </Modal.Dialog>
            </div>
        );
    }

    renderInfo(modalConfig) {
        const okFunction = this.onOk.bind(this, modalConfig);
        return (
            <div className="static-modal">
                <Modal.Dialog bsSize={this.getModalSize(modalConfig)}>
                    <Modal.Header>
                        <Modal.Title>{modalConfig.modalTitle}</Modal.Title>
                    </Modal.Header>

                    <Modal.Body>
                        {modalConfig.modalMessage}
                    </Modal.Body>

                    <Modal.Footer>
                        <Button autoFocus type="primary" onClick={okFunction}>OK</Button>
                    </Modal.Footer>

                </Modal.Dialog>
            </div>
        );
    }

    renderCustom(modalConfig) {
        return (
            <div className="static-modal">
                <Modal.Dialog bsSize={this.getModalSize(modalConfig)}>
                    {modalConfig.modalComponent}
                </Modal.Dialog>
            </div>
        );
    }

    renderDownload(modalConfig) {
        const downloadFunction = this.onOk.bind(this, modalConfig);
        const cancelFunction = this.onCancel.bind(this, modalConfig);
        const downloadUrl = modalConfig.downloadUrl;

        return (
            <div className="static-modal">
                <Modal.Dialog bsSize={this.getModalSize(modalConfig)}>
                    <Modal.Header>
                        <Modal.Title>{modalConfig.modalTitle}</Modal.Title>
                    </Modal.Header>

                    <Modal.Body>
                        {modalConfig.modalMessage}
                    </Modal.Body>

                    <Modal.Footer>
                        <Button autoFocus type="primary" href={downloadUrl} onClick={downloadFunction}>Download</Button>
                        <Button onClick={cancelFunction}>Cancel</Button>
                    </Modal.Footer>

                </Modal.Dialog>
            </div>
        );
    }

    render() {
        let component = (<div />);
        // Get the top modal config that is not a status message!
        const modalConfig = this.getTopModalConfig();

        if (modalConfig) {
            if (modalConfig.modalType === ModalsService.TYPE_ERROR) {
                component = this.renderError(modalConfig);

            } else if (modalConfig.modalType === ModalsService.TYPE_PROGRESS) {
                component = this.renderProgress(modalConfig);

            } else if (modalConfig.modalType === ModalsService.TYPE_CONFIRM) {
                component = this.renderConfirm(modalConfig);

            } else if (modalConfig.modalType === ModalsService.TYPE_INFO) {
                component = this.renderInfo(modalConfig);

            } else if (modalConfig.modalType === ModalsService.TYPE_CUSTOM) {
                component = this.renderCustom(modalConfig);
            
            } else if (modalConfig.modalType === ModalsService.TYPE_DOWNLOAD) {
                component = this.renderDownload(modalConfig);
            }
            // Wrap in custom style if needed
            const className = modalConfig.className ? modalConfig.className : '';
            component = <div className={className}>{component}</div>;

        }

        return (
            <div>
                {component}
                {this.renderStatusMessages()}
            </div>
        );
    }
}

// Automatically pull state information from the store and
// pass it to this component as props
const mapStateToProps = (storeState) => {
    const myState = ModalsService.getMyState(storeState);
    const securityState = SecurityService.getMyState(storeState);
    const userLoggedIn = securityState.userLoggedIn;
    return {
        modals: myState.openModals,
        userLoggedIn
    };
};


export default connect(mapStateToProps)(Modals);



// WEBPACK FOOTER //
// ./src/components/core/modals/Modals.js